import React, { useState, useEffect, useRef } from 'react';
import { getVideoStreamUrl } from '../../services/subjects.util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlay, faPause, faSpinner, faShieldAlt, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import './VideoPlayer.css';

const isNativeMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  if (window.ReactNativeWebView || window.triggerRazorpayPayment) return true;
  const ua = navigator.userAgent || '';
  const isMobileUA = /iPhone|iPad|iPod|Android/i.test(ua);
  const isWebView = /; wv\)|WebView/i.test(ua);
  const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isMobileUA || isIpadOs || isWebView;
};

const formatPlaybackTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

const isLandscapeViewport = () => {
  if (typeof window === 'undefined') return false;

  // Prefer live viewport size — Android WebViews often leave screen.orientation.type
  // locked/stale (e.g. portrait) even after the page has rotated.
  const width = window.visualViewport?.width || window.innerWidth || 0;
  const height = window.visualViewport?.height || window.innerHeight || 0;
  if (width > 0 && height > 0) {
    return width > height;
  }

  if (typeof window.matchMedia === 'function') {
    try {
      if (window.matchMedia('(orientation: landscape)').matches) return true;
    } catch (_) {
      // ignore unsupported media queries
    }
  }

  // Legacy Android / older WebViews
  if (typeof window.orientation === 'number') {
    return Math.abs(window.orientation) === 90;
  }

  return !!window.screen?.orientation?.type?.startsWith('landscape');
};

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.webkitCurrentFullScreenElement ||
  null;

const isReactNativeWebView = () =>
  typeof window !== 'undefined' &&
  !!(window.ReactNativeWebView || window.sendToReactNative || window.__IS_RN_WEBVIEW__);

const requestNativeOrientation = (orientation) => {
  if (!isReactNativeWebView()) return;
  const message = { type: 'ORIENTATION_LOCK', orientation };
  try {
    if (typeof window.sendToReactNative === 'function') {
      window.sendToReactNative(message);
      return;
    }
    window.ReactNativeWebView?.postMessage?.(JSON.stringify(message));
  } catch (err) {
    console.warn('Failed to request native orientation:', err);
  }
};

const VideoPlayer = ({ video, onClose }) => {
  const [streamUrl, setStreamUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLandscapeFullscreen, setIsLandscapeFullscreen] = useState(
    () => isNativeMobileDevice() && isLandscapeViewport()
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const isMobile = useRef(isNativeMobileDevice());
  const isRnWebView = useRef(isReactNativeWebView());

  useEffect(() => {
    const fetchStreamUrl = async () => {
      if (!video?._id) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await getVideoStreamUrl(video._id);
        
        if (response.success && response.data?.streamUrl) {
          setStreamUrl(response.data.streamUrl);
        } else {
          throw new Error('Failed to get video stream URL');
        }
      } catch (err) {
        console.error('Error fetching stream URL:', err);
        setError(err.message || 'Failed to load video');
      } finally {
        setLoading(false);
      }
    };

    fetchStreamUrl();
  }, [video]);

  // Security: Disable right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Security: Disable drag
  const handleDragStart = (e) => {
    e.preventDefault();
    return false;
  };

  const isVideoInNativeFullscreen = () => {
    const media = videoRef.current;
    return !!(
      getFullscreenElement() ||
      media?.webkitDisplayingFullscreen ||
      media?.webkitPresentationMode === 'fullscreen'
    );
  };

  const exitNativeFullscreen = async () => {
    const media = videoRef.current;
    if (media?.webkitPresentationMode === 'fullscreen' && media.webkitSetPresentationMode) {
      media.webkitSetPresentationMode('inline');
      return;
    }
    if (media?.webkitExitFullscreen) {
      media.webkitExitFullscreen();
      return;
    }
    if (document.exitFullscreen) {
      await document.exitFullscreen();
      return;
    }
    if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  };

  const exitCssFullscreen = () => {
    setIsFullscreen(false);
    if (isRnWebView.current) {
      requestNativeOrientation('portrait');
    }
  };

  const toggleFullscreen = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const videoEl = videoRef.current;
    const container = containerRef.current;
    if (!videoEl) return;

    // React Native WebView: lock landscape + CSS fullscreen (skip OS Fullscreen API).
    if (isRnWebView.current) {
      if (isFullscreen || isLandscapeFullscreen) {
        exitCssFullscreen();
        return;
      }
      requestNativeOrientation('landscape');
      setIsFullscreen(true);
      return;
    }

    if (isVideoInNativeFullscreen()) {
      try {
        await exitNativeFullscreen();
      } catch (err) {
        console.warn('Failed to exit native fullscreen:', err);
      }
      setIsFullscreen(false);
      return;
    }

    if (isFullscreen) {
      setIsFullscreen(false);
      return;
    }

    // iOS / WKWebView: native video presentation (not the Fullscreen API)
    if (typeof videoEl.webkitEnterFullscreen === 'function') {
      try {
        videoEl.webkitEnterFullscreen();
        window.setTimeout(() => {
          if (!isVideoInNativeFullscreen()) {
            setIsFullscreen(true);
          }
        }, 250);
        return;
      } catch (err) {
        console.warn('webkitEnterFullscreen failed, using CSS fallback:', err);
        setIsFullscreen(true);
        return;
      }
    }

    const target = container || videoEl;
    try {
      if (target.requestFullscreen) {
        await target.requestFullscreen();
        return;
      }
      if (target.webkitRequestFullscreen) {
        target.webkitRequestFullscreen();
        return;
      }
    } catch (err) {
      console.warn('Fullscreen API unavailable, using CSS fallback:', err);
    }

    // Native mobiles / WebViews often have fullscreenEnabled === false
    setIsFullscreen(true);
  };

  // Security: Disable keyboard shortcuts for downloading/copying
  const handleKeyDown = (e) => {
    // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U, Ctrl+S, Ctrl+A, Ctrl+C, Ctrl+V
    if (
      e.keyCode === 123 || // F12
      (e.ctrlKey && e.shiftKey && e.keyCode === 73) || // Ctrl+Shift+I
      (e.ctrlKey && e.shiftKey && e.keyCode === 74) || // Ctrl+Shift+J
      (e.ctrlKey && e.keyCode === 85) || // Ctrl+U
      (e.ctrlKey && e.keyCode === 83) || // Ctrl+S
      (e.ctrlKey && e.keyCode === 65) || // Ctrl+A
      (e.ctrlKey && e.keyCode === 67) || // Ctrl+C
      (e.ctrlKey && e.keyCode === 86) || // Ctrl+V
      (e.ctrlKey && e.keyCode === 88) || // Ctrl+X
      e.keyCode === 44 // Print Screen
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    if (e.key === 'Escape') {
      if (isVideoInNativeFullscreen() || isFullscreen) {
        exitNativeFullscreen().catch(() => {});
        exitCssFullscreen();
        return;
      }
      // Landscape CSS fullscreen clears when the device returns to portrait — do not close.
      if (isLandscapeFullscreen) {
        return;
      }
      handleClose();
    }
  };

  const handleClose = () => {
    if (isRnWebView.current) {
      requestNativeOrientation('portrait');
    }
    onClose();
  };

  // Restore portrait if the player unmounts while landscape-locked (native app).
  useEffect(() => {
    if (!isRnWebView.current) return undefined;
    return () => {
      requestNativeOrientation('portrait');
    };
  }, []);

  // Android hardware back restores portrait and notifies the WebView.
  useEffect(() => {
    if (!isRnWebView.current) return undefined;

    const onNativeOrientation = (event) => {
      const detail = event?.detail;
      if (detail?.type === 'ORIENTATION_LOCK' && detail?.orientation === 'portrait') {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('nativeOrientation', onNativeOrientation);
    return () => window.removeEventListener('nativeOrientation', onNativeOrientation);
  }, []);

  // Security: Detect developer tools (desktop only — mobile chrome trips the size heuristic)
  useEffect(() => {
    if (isMobile.current) return undefined;

    const detectDevTools = () => {
      const threshold = 160;
      
      if (
        window.outerHeight - window.innerHeight > threshold ||
        window.outerWidth - window.innerWidth > threshold
      ) {
        setDevToolsOpen(true);
      } else {
        setDevToolsOpen(false);
      }
    };

    detectDevTools();
    const interval = setInterval(detectDevTools, 1000);

    return () => clearInterval(interval);
  }, []);

  // Native only: landscape rotation uses CSS fullscreen (no Fullscreen API — no user gesture).
  useEffect(() => {
    if (!isMobile.current) return undefined;

    let debounceTimer = null;
    const pendingTimers = [];

    const clearPendingTimers = () => {
      while (pendingTimers.length) {
        window.clearTimeout(pendingTimers.pop());
      }
    };

    const syncLandscape = () => {
      setIsLandscapeFullscreen(isLandscapeViewport());
    };

    // Android WebView updates innerWidth/innerHeight after orientationchange — recheck with delays.
    const syncLandscapeWithRetry = () => {
      clearPendingTimers();
      syncLandscape();
      [50, 150, 350].forEach((ms) => {
        pendingTimers.push(window.setTimeout(syncLandscape, ms));
      });
    };

    const onResize = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(syncLandscape, 50);
    };

    syncLandscape();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', syncLandscapeWithRetry);

    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener?.('resize', onResize);
    visualViewport?.addEventListener?.('scroll', onResize);

    const orientation = window.screen?.orientation;
    orientation?.addEventListener?.('change', syncLandscapeWithRetry);

    let mediaQuery = null;
    const onMediaChange = () => syncLandscapeWithRetry();
    try {
      mediaQuery = window.matchMedia?.('(orientation: landscape)');
      if (mediaQuery?.addEventListener) {
        mediaQuery.addEventListener('change', onMediaChange);
      } else if (mediaQuery?.addListener) {
        mediaQuery.addListener(onMediaChange);
      }
    } catch (_) {
      mediaQuery = null;
    }

    return () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      clearPendingTimers();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', syncLandscapeWithRetry);
      visualViewport?.removeEventListener?.('resize', onResize);
      visualViewport?.removeEventListener?.('scroll', onResize);
      orientation?.removeEventListener?.('change', syncLandscapeWithRetry);
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener('change', onMediaChange);
      } else if (mediaQuery?.removeListener) {
        mediaQuery.removeListener(onMediaChange);
      }
    };
  }, []);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !isMobile.current) return undefined;

    const syncPlayback = () => {
      setIsPlaying(!videoEl.paused);
      setCurrentTime(videoEl.currentTime || 0);
      setDuration(videoEl.duration || 0);
    };

    videoEl.addEventListener('play', syncPlayback);
    videoEl.addEventListener('pause', syncPlayback);
    videoEl.addEventListener('timeupdate', syncPlayback);
    videoEl.addEventListener('loadedmetadata', syncPlayback);
    videoEl.addEventListener('durationchange', syncPlayback);
    syncPlayback();

    return () => {
      videoEl.removeEventListener('play', syncPlayback);
      videoEl.removeEventListener('pause', syncPlayback);
      videoEl.removeEventListener('timeupdate', syncPlayback);
      videoEl.removeEventListener('loadedmetadata', syncPlayback);
      videoEl.removeEventListener('durationchange', syncPlayback);
    };
  }, [streamUrl, loading, error]);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(isVideoInNativeFullscreen());
    };

    const videoEl = videoRef.current;
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);
    videoEl?.addEventListener('webkitbeginfullscreen', syncFullscreenState);
    videoEl?.addEventListener('webkitendfullscreen', syncFullscreenState);
    videoEl?.addEventListener('webkitpresentationmodechanged', syncFullscreenState);

    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
      videoEl?.removeEventListener('webkitbeginfullscreen', syncFullscreenState);
      videoEl?.removeEventListener('webkitendfullscreen', syncFullscreenState);
      videoEl?.removeEventListener('webkitpresentationmodechanged', syncFullscreenState);
    };
  }, [streamUrl]);

  // Security: Disable video element inspection
  const handleVideoLoad = () => {
    if (videoRef.current) {
      // Disable picture-in-picture
      videoRef.current.disablePictureInPicture = true;
      videoRef.current.setAttribute('playsinline', 'true');
      videoRef.current.setAttribute('webkit-playsinline', 'true');
      if (isMobile.current) {
        videoRef.current.controls = false;
        videoRef.current.removeAttribute('controls');
      }
      videoRef.current.setAttribute(
        'controlslist',
        isMobile.current ? 'nodownload noremoteplayback nofullscreen' : 'nodownload noremoteplayback'
      );
      if (videoRef.current.controlsList?.add) {
        videoRef.current.controlsList.add('nodownload');
        videoRef.current.controlsList.add('noremoteplayback');
        if (isMobile.current) {
          videoRef.current.controlsList.add('nofullscreen');
        } else if (videoRef.current.controlsList.remove) {
          videoRef.current.controlsList.remove('nofullscreen');
        }
      }
      
      // Remove download attribute if present
      videoRef.current.removeAttribute('download');
      
      // Disable right-click on video specifically
      videoRef.current.addEventListener('contextmenu', handleContextMenu);
    }
  };

  // Handle background click to close
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget && !isFullscreen && !isLandscapeFullscreen) {
      handleClose();
    }
  };

  // Watermark overlay — skip on native mobile; it sits on the video and
  // disables the player fullscreen control in WebViews / iOS.
  const SecurityOverlay = () => (
    <div 
      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        background: 'linear-gradient(45deg, transparent 45%, rgba(255,255,255,0.1) 50%, transparent 55%)',
        pointerEvents: 'none',
        zIndex: 10,
        userSelect: 'none'
      }}
    >
      <div 
        className="text-white opacity-25 position-absolute"
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          transform: 'rotate(-45deg)',
          userSelect: 'none',
          pointerEvents: 'none'
        }}
      >
        ©gvkEDU Student Portal - Authorized Access Only
      </div>
    </div>
  );

  // Security: Dev tools detection warning
  if (devToolsOpen) {
    return (
      <div 
        className="video-player-modal position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1050
        }}
      >
        <div className="text-center text-white">
          <FontAwesomeIcon icon={faShieldAlt} size="4x" className="text-danger mb-4" />
          <h3 className="mb-3">Security Warning</h3>
          <p className="mb-4">Developer tools detected. Please close developer tools to continue watching.</p>
          <button onClick={handleClose} className="btn btn-primary">
            Close Video Player
          </button>
        </div>
      </div>
    );
  }

  // User expand OR native landscape — never rely on OS Fullscreen API for rotate.
  const usingCssFullscreen =
    (isFullscreen || isLandscapeFullscreen) && !isVideoInNativeFullscreen();

  const togglePlayback = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const videoEl = videoRef.current;
    if (!videoEl) return;
    if (videoEl.paused) {
      videoEl.play().catch(() => {});
    } else {
      videoEl.pause();
    }
  };

  const handleSeek = (e) => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    const nextTime = Number(e.target.value);
    if (Number.isFinite(nextTime)) {
      videoEl.currentTime = nextTime;
      setCurrentTime(nextTime);
    }
  };

  return (
    <div 
      ref={containerRef}
      className={[
        'video-player-modal',
        'position-fixed',
        'top-0',
        'start-0',
        'w-100',
        'h-100',
        'd-flex',
        'align-items-center',
        'justify-content-center',
        isMobile.current ? 'video-player-native' : '',
        isMobile.current && isLandscapeFullscreen ? 'is-landscape' : '',
        usingCssFullscreen ? 'is-css-fullscreen' : ''
      ].filter(Boolean).join(' ')}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 1050,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none'
      }}
      onClick={handleBackgroundClick}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div 
        className="video-player-container bg-dark position-relative"
        style={{
          maxWidth: usingCssFullscreen ? '100vw' : '90vw',
          maxHeight: usingCssFullscreen ? '100vh' : '90vh',
          width: usingCssFullscreen ? '100%' : '1000px',
          height: usingCssFullscreen ? '100%' : 'auto',
          borderRadius: usingCssFullscreen ? 0 : '0.375rem',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
      >
        {/* Header */}
        {!usingCssFullscreen && (
          <div className="video-player-header d-flex justify-content-between align-items-center p-3 border-bottom border-secondary">
            <div className="video-player-heading">
              <h5 className="video-player-title text-white mb-1" style={{ userSelect: 'none' }}>
                {video?.title || 'Video Player'}
              </h5>
              {video?.description && (
                <small className="video-player-description text-light opacity-75" style={{ userSelect: 'none' }}>
                  {video.description}
                </small>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {streamUrl && !loading && !error && !isMobile.current && (
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="btn btn-outline-light btn-sm"
                  aria-label="Enter fullscreen"
                >
                  <FontAwesomeIcon icon={faExpand} />
                </button>
              )}
              <button 
                onClick={handleClose}
                className="btn btn-outline-light btn-sm"
                aria-label="Close video player"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}

        {/* Video Content */}
        <div
          className="video-player-content position-relative"
          style={usingCssFullscreen ? { height: '100%' } : undefined}
        >
          {usingCssFullscreen && !isMobile.current && (
            <button
              type="button"
              onClick={toggleFullscreen}
              className="btn btn-outline-light btn-sm position-absolute"
              aria-label="Exit fullscreen"
              style={{ top: 12, right: 12, zIndex: 20 }}
            >
              <FontAwesomeIcon icon={faCompress} />
            </button>
          )}
          {loading && (
            <div className="text-center py-5">
              <FontAwesomeIcon 
                icon={faSpinner} 
                spin 
                size="2x" 
                className="text-primary mb-3" 
              />
              <div className="text-white h5 mb-2">Loading video...</div>
              <div className="text-light">Please wait while we prepare your video</div>
            </div>
          )}

          {error && (
            <div className="text-center py-5">
              <div className="text-danger mb-3">
                <FontAwesomeIcon icon={faPlay} size="3x" />
              </div>
              <div className="text-white h5 mb-2">Error Loading Video</div>
              <div className="text-light mb-3">{error}</div>
              <button 
                onClick={handleClose}
                className="btn btn-primary"
              >
                Close
              </button>
            </div>
          )}

          {streamUrl && !loading && !error && (
            <div className="position-relative" style={usingCssFullscreen ? { height: '100%' } : undefined}>
              {!isMobile.current && <SecurityOverlay />}
              <video 
                ref={videoRef}
                src={streamUrl}
                controls={!isMobile.current}
                autoPlay
                muted={false}
                playsInline
                disablePictureInPicture
                controlsList={isMobile.current ? 'nodownload noremoteplayback nofullscreen' : 'nodownload noremoteplayback'}
                onLoadedData={handleVideoLoad}
                onContextMenu={handleContextMenu}
                onDragStart={handleDragStart}
                onClick={isMobile.current ? togglePlayback : undefined}
                className="w-100"
                style={{
                  height: usingCssFullscreen ? '100%' : 'auto',
                  maxHeight: usingCssFullscreen ? '100vh' : '70vh',
                  objectFit: 'contain',
                  backgroundColor: '#000',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  pointerEvents: 'auto',
                  display: 'block'
                }}
                onError={(e) => {
                  console.error('Video playback error:', e);
                  setError('Failed to play video. The video file may be corrupted or incompatible.');
                }}
              >
                <source src={streamUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              {isMobile.current && (
                <div className="video-native-controls">
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={togglePlayback}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                  </button>
                  <span className="time-label">{formatPlaybackTime(currentTime)}</span>
                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.1}
                    value={Math.min(currentTime, duration || 0)}
                    onChange={handleSeek}
                    aria-label="Seek"
                  />
                  <span className="time-label">{formatPlaybackTime(duration)}</span>
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={toggleFullscreen}
                    aria-label={usingCssFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  >
                    <FontAwesomeIcon icon={usingCssFullscreen ? faCompress : faExpand} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer with video info */}
        {video && !usingCssFullscreen && (
          <div className="video-player-footer p-3 border-top border-secondary">
            <div className="d-flex gap-3 text-light" style={{ userSelect: 'none' }}>
              {video.createdAt && (
                <small>
                  <strong>Published:</strong> {new Date(video.createdAt).toLocaleDateString()}
                </small>
              )}
              {video.durationSec && (
                <small>
                  <strong>Duration:</strong> {Math.floor(video.durationSec / 60)}:{(video.durationSec % 60).toString().padStart(2, '0')}
                </small>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
